import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { z } from 'zod';

import { Bulletin, useBulletin } from '@autopass-web/feature-bulletin';
import {
  EventScope,
  useClickEvent,
  useInViewedEvent,
} from '@autopass-web/mochi-analytics/event';
import { MochiButton } from '@autopass-web/mochi-component';

import { AuthorizedPaymentInfoCard } from '../components/authorized-payment-info-card';
import { DirectPaymentInfoCard } from '../components/direct-payment-info-card';
import { MainLayout } from '../components/main-layout';
import { PATHS } from '../configs';
import { environment } from '../environments/environment';
import { analyticsService } from '../services/analytics';
import { axiosInstance } from '../services/client';
import { useInvoice } from '../services/use-invoice';

import type { ChainStoreTransactionType } from '@autopass-web/types';

const ENTRY_BOARD_CODE = 'payment_info_page';

const EntryBulletin = ({
  transaction_type,
}: {
  transaction_type: ChainStoreTransactionType;
}) => {
  const bulletin = useBulletin({
    clientInstance: axiosInstance,
    boardCode: ENTRY_BOARD_CODE,
    attributesSchema: z.object({
      image_url: z.string(),
      transaction_type: z.array(z.string()),
    }),
  });

  const handleClickEvent = useClickEvent('廣告曝光');
  const handleInViewEvent = useInViewedEvent('廣告曝光');

  const availBoards = (bulletin.data ?? []).filter((d) => {
    return d.attributes.transaction_type.includes(transaction_type);
  });

  return (
    <div>
      <Bulletin
        {...bulletin}
        data={availBoards}
        handleBoardClick={(bulletin) => {
          handleClickEvent({
            廣告id: bulletin.id,
            版位id: ENTRY_BOARD_CODE,
          });
        }}
        handleBoardInView={(bulletin) => {
          handleInViewEvent(
            {
              廣告id: bulletin.id,
              版位id: ENTRY_BOARD_CODE,
            },
            { key: bulletin.id },
          );
        }}
      />
    </div>
  );
};

export const Entry = () => {
  const { invoiceId } = useParams();

  const isInvoiceIdValid = typeof invoiceId === 'string';

  const { invoice } = useInvoice(
    { invoiceId },
    {
      enabled: isInvoiceIdValid,
    },
  );
  const {
    chain_store_name,
    transaction_type_i18n,
    transaction_type,
    amount,
    capture_method,
  } = invoice;

  const navigate = useNavigate();

  const handleNext = useCallback(() => {
    analyticsService.entryNextButtonClick({
      chainStorePartnerName: invoice.chain_store_partner_name,
      chainStoreName: invoice.chain_store_name,
      transactionTypeI18N: invoice.transaction_type_i18n,
      captureType: invoice.capture_method,
    });

    navigate(PATHS.INVOICES(invoiceId)['']);
  }, [navigate, invoice, invoiceId]);

  useEffect(() => {
    analyticsService.entryPageView({
      chainStorePartnerName: invoice.chain_store_partner_name,
      chainStoreName: invoice.chain_store_name,
      transactionTypeI18N: invoice.transaction_type_i18n,
      captureType: invoice.capture_method,
      amount: invoice.item_amount,
    });
  }, [
    invoice.capture_method,
    invoice.chain_store_name,
    invoice.chain_store_partner_name,
    invoice.item_amount,
    invoice.transaction_type_i18n,
  ]);

  return (
    <EventScope.Page page={'交易內容頁'} props={{ 場站名稱: chain_store_name }}>
      <MainLayout>
        <div
          className={clsx(
            'flex-col',
            'flex',
            'w-full',
            'h-full',
            'relative',
            'pt-20',
            'px-5',
            'overflow-x-hidden',
            'overflow-y-auto',
            'before:content-[""]',
            'before:h-[280px]',
            'before:w-full',
            'before:absolute',
            'before:top-0',
            'before:left-0',
            'before:bg-[linear-gradient(135deg,#5aaaff,#0075ff_70%)]',
            'before:z-[-1]',
          )}
        >
          {capture_method === 'automatic' ? (
            <DirectPaymentInfoCard
              chainStoreName={chain_store_name}
              transactionTypeI18n={transaction_type_i18n}
              amount={amount}
            />
          ) : (
            <AuthorizedPaymentInfoCard
              chainStoreName={chain_store_name}
              transactionTypeI18n={transaction_type_i18n}
              amount={amount}
            />
          )}
          <div className="text-[13px] text-center text-[#999999] mt-10">
            開始服務即代表同意
            <a
              href={environment.termsOfServiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={analyticsService.entryTermsOfServiceClick}
            >
              服務條款
            </a>
            &nbsp;/&nbsp;
            <a
              href={environment.privacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={analyticsService.entryPrivacyPolicyClick}
            >
              隱私權政策
            </a>
          </div>
          <MochiButton
            className="mx-10 mt-4"
            variant="solid-round"
            onClick={handleNext}
          >
            下一步
          </MochiButton>
        </div>
        <EntryBulletin transaction_type={transaction_type} />
      </MainLayout>
    </EventScope.Page>
  );
};
